import { isAxiosError } from 'axios';

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The gateway prefixes some failures with a bracketed status - e.g.
 * `Bad Request [400]: vendor already assigned`. The prefix is noise to a user, so
 * anything before the last `]:` is stripped.
 */
const stripStatusPrefix = (message: string): string => {
  const marker = ']:';
  const index = message.lastIndexOf(marker);
  return index === -1 ? message.trim() : message.slice(index + marker.length).trim();
};

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
};

/**
 * Axios reports the same `ERR_NETWORK` for every response the browser refuses to
 * hand back: a genuinely offline device, a DNS/TLS failure, and — the common one
 * against our API Gateway — a 4xx whose gateway response (ACCESS_DENIED,
 * UNAUTHORIZED, DEFAULT_4XX) carries no `Access-Control-Allow-Origin` header.
 * The browser blocks that response, so `error.response` never arrives and the
 * status is unreadable from JS.
 *
 * Some builds surface it as `message: 'Network Error'` with no `code`, so both
 * are matched.
 */
export const isNetworkLevelError = (error: unknown): boolean => {
  const candidate = error as { code?: unknown; message?: unknown } | null;
  return candidate?.code === 'ERR_NETWORK' || candidate?.message === 'Network Error';
};

const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Until those gateway responses send CORS headers, `navigator.onLine` is the only
 * signal separating "no network" from "the server answered something we are not
 * allowed to read" — telling an online user to check their connection sends them
 * looking in the wrong place.
 */
export const getNetworkErrorMessage = (): string =>
  isBrowserOffline()
    ? 'No internet connection. Please check your network and try again.'
    : "Couldn't reach the server. The request was rejected or your session expired — " +
      'please refresh and try again.';

/**
 * Pulls a human-readable message out of a failed request.
 *
 * The backend is inconsistent about which field carries the reason - across
 * endpoints it has been `errorMessage`, `error`, and `message` - so all three are
 * tried, in the order the legacy client used, before falling back.
 *
 * Handles both transport failures (Axios rejects) and business failures (HTTP 200
 * with `is_success: false`), because callers should not have to care which they
 * got. Pass the response *body* for the latter.
 */
export const getApiErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (isNetworkLevelError(error)) return getNetworkErrorMessage();
    const data = error.response?.data as Record<string, unknown> | undefined;
    /**
     * Deliberately does NOT fall back to `error.message`. Axios fills that with
     * "Request failed with status code 400", which reads as a bug report rather
     * than something the user can act on - and this string goes straight into a
     * toast. The legacy client made the same choice.
     */
    const message = firstString(data?.errorMessage, data?.error, data?.message);
    return message ? stripStatusPrefix(message) : FALLBACK_MESSAGE;
  }

  if (error && typeof error === 'object') {
    const data = error as Record<string, unknown>;
    const message = firstString(data.errorMessage, data.error, data.message);
    if (message) return stripStatusPrefix(message);
  }

  if (error instanceof Error) return error.message;

  return FALLBACK_MESSAGE;
};

/**
 * "Record Not Found" is the backend's way of saying "empty", not "broken". It
 * reaches the client as a business failure on plenty of list endpoints, and
 * surfacing it as an error toast trains users to ignore toasts.
 */
export const isRecordNotFound = (message: string | undefined): boolean =>
  !!message && message.toLowerCase().includes('record not found');

/**
 * Normalises a list payload into an array.
 *
 * **These endpoints return a bare object when there is exactly one row**, an
 * array when there are several, and `null` when there are none. Writing
 * `result ?? []` reads as though it handles that, but it does not: a bare object
 * is not null, so it passes straight through and the screen crashes on `.map is
 * not a function` - which took out the platform-invoices page.
 *
 * Every list hook should go through this rather than reasoning about it again.
 */
export const toRows = <T>(result: T[] | T | null | undefined): T[] => {
  if (result == null) return [];
  return Array.isArray(result) ? result : [result];
};

/**
 * The row count from a list response, whichever way it was reported.
 *
 * **Four conventions are in use across this API** and there is no way to tell
 * which from the endpoint's shape: `total_count` beside the result, `result_count`
 * beside it, or - inside a `pagination` wrapper - either `total` or `total_count`.
 * The member-list and document endpoints use `pagination.total`; reading only the
 * flat pair gives 0, which silently disables paging because the table believes
 * there is a single page.
 *
 * `pagination.total_count` is the fourth, and `get_group_link_list` is the endpoint
 * that sends it - which is why the MLS portal's Linked Accounts list drew no
 * paginator at all. Its footer is the `band` variant, and that one hides itself
 * entirely at a count of 0 rather than rendering an empty readout, so a count this
 * function could not find read as a list that simply has no pages. The legacy
 * screen reads the same field (`DataVendors/LinkedAccounts/index.tsx`), and it is
 * the only place in that app that does.
 *
 * Order matters only in that the wrapper is the last resort: a response carries
 * one of these spellings, not several, so the chain is a search rather than a
 * precedence rule.
 *
 * A missing count is 0, which is right: nothing to page through.
 */
export const totalOf = (body: {
  total_count?: number | null;
  result_count?: number | null;
  pagination?: { total?: number | null; total_count?: number | null } | null;
}): number =>
  body?.total_count ??
  body?.result_count ??
  body?.pagination?.total ??
  body?.pagination?.total_count ??
  0;
